import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  reference: string
}

function makeReference(): string {
  const bytes = new Uint8Array(4)
  crypto.getRandomValues(bytes)
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 4)}-${hex.slice(4)}`
}

/**
 * The last line of defence. Never shows a stack trace or an HTTP code as the
 * headline; shows a plain sentence, a way out, and a reference the user can
 * quote to support.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, reference: '' }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, reference: makeReference() }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Unhandled error in the React tree', error, info.componentStack)
  }

  render(): ReactNode {
    const { error, reference } = this.state
    if (!error) return this.props.children

    return (
      <div className="app">
        <main className="page" id="main" tabIndex={-1}>
          <div className="container container--prose">
            <div className="state-page">
              <p className="state-page__code">Error</p>
              <h1 className="state-page__title">Something went wrong</h1>
              <p className="state-page__body">This one is on us, not you. Try again in a moment.</p>
              <p className="u-caption u-numeric">Reference: {reference}</p>
              <div className="btn-group">
                <button
                  className="btn btn--primary"
                  type="button"
                  onClick={() => window.location.reload()}
                >
                  Try again
                </button>
                <a className="btn btn--secondary" href="/">
                  Go to home
                </a>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }
}
